import { SignedSaleOrder } from "@/api/orders/types";
import { useWeb3State } from "@/web3/context";
import useERC20AllowanceQuery from "@/web3/contracts/common/actions/useERC20AllowanceQuery";
import useExchangeERC20ApprovalMutation from "@/web3/contracts/common/actions/useExchangeERC20ApprovalMutation";
import { useCallback, useMemo, useState } from "react";
import { getChainContracts } from "../config/blockchain/helpers";
import { isEqual } from "../string";
import useAccountBalance from "./useAccountBalance";
import usePaymentCurrency from "./usePaymentCurrency";

const useWithFundsCheck = (fn: any, order: SignedSaleOrder) => {
  const { currentAddress, chainId } = useWeb3State();

  const [error, setError] = useState<string>();

  const paymentMethod = usePaymentCurrency(order.paymentErc20TokenAddress);

  const contracts = useMemo(() => getChainContracts(chainId), [chainId]);

  const { allowance } = useERC20AllowanceQuery(
    order.buyer,
    contracts?.exchangeAddress,
    order.paymentErc20TokenAddress,
  );

  const { balanceInRaw } = useAccountBalance(
    order.paymentErc20TokenAddress,
    order.buyer,
  );
  const { approve } = useExchangeERC20ApprovalMutation(
    order.paymentErc20TokenAddress,
  );

  const FundsErrorMessage = useMemo(
    () => ({
      SellerError:
        "Unable to finalize the trade at this time. Try again later.",
      BuyerError: `You don't have enough ${paymentMethod?.label}.`,
    }),
    [paymentMethod?.label],
  );

  const checkedFn = useCallback(
    async (...args: any[]) => {
      if (
        (!allowance && allowance !== BigInt(0)) ||
        (!balanceInRaw && balanceInRaw !== BigInt(0))
      ) {
        return;
      }

      const requiredAmount = BigInt(order.totalPrice || order.price!);

      const isSufficientBalance = requiredAmount <= balanceInRaw;
      const isSufficientAllowance = requiredAmount <= allowance;

      if (isSufficientBalance && isSufficientAllowance) {
        return await fn(...args);
      }

      const meSeller = isEqual(currentAddress, order.seller);
      if (meSeller) {
        return setError(FundsErrorMessage.SellerError);
      }

      if (!isSufficientBalance) {
        return setError(FundsErrorMessage.BuyerError);
      }

      await approve(requiredAmount);

      return await fn(...args);
    },
    [
      FundsErrorMessage,
      allowance,
      approve,
      balanceInRaw,
      currentAddress,
      fn,
      order,
    ],
  );

  return useMemo(() => ({ checkedFn, error }), [checkedFn, error]);
};

export default useWithFundsCheck;
